import React, { useCallback, useEffect, useRef, useState } from "react";
import { API } from "@/lib/api";

// SPIA DI RETE (web). Sul telefono il puntino dice se stai passando dal tunnel mimetizzato.
// Dal computer quel tunnel NON esiste: vive dentro l'app Android e un browser non può
// avviarlo. Dire "sei protetto" qui sarebbe una bugia, e le bugie sulla privacy sono la
// cosa peggiore che possiamo fare. Quindi questa spia dice la verità utile:
// a quale nodo sei attaccato, con quale protocollo, con quanti millisecondi di ritardo.
//   verde  = nodo raggiungibile e veloce
//   ambra  = risponde ma lento o instabile
//   rosso  = nodo non raggiungibile (sei scollegato)
const HOST = (() => { try { return new URL(API).host; } catch { return "—"; } })();

export default function SpiaRete() {
  const [ms, setMs] = useState(null);
  const [proto, setProto] = useState("");
  const [errore, setErrore] = useState("");
  const [aperto, setAperto] = useState(false);
  const inCorso = useRef(false);

  const misura = useCallback(async () => {
    if (inCorso.current || document.visibilityState !== "visible") return;
    inCorso.current = true;
    const t0 = performance.now();
    try {
      const r = await fetch(API + "/health", { cache: "no-store" });
      if (!r.ok) throw new Error("HTTP " + r.status);
      setMs(Math.round(performance.now() - t0));
      setErrore("");
      // Quale strada ha preso davvero il browser: HTTP/3, HTTP/2 o vecchio HTTP/1.1.
      try {
        const e = performance.getEntriesByType("resource").filter((x) => x.name.indexOf("/api/health") >= 0 && x.nextHopProtocol).pop();
        // Ripiego: se la richiesta non riporta la strada presa, si guarda quella con cui è
        // arrivata la pagina. Un trattino al posto del protocollo sembra un guasto.
        const n = performance.getEntriesByType("navigation")[0];
        const via = (e && e.nextHopProtocol) || (n && n.nextHopProtocol) || "";
        if (via) setProto(via);
      } catch { /* niente */ }
    } catch (e) {
      setMs(null);
      setErrore(String((e && e.message) || e));
    } finally { inCorso.current = false; }
  }, []);

  useEffect(() => {
    misura();
    const iv = setInterval(misura, 20000);
    const onVis = () => { if (document.visibilityState === "visible") misura(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(iv); document.removeEventListener("visibilitychange", onVis); };
  }, [misura]);

  const colore = errore ? "#ff5b5b" : (ms === null ? "#565c59" : (ms > 800 ? "#F4C430" : "#22C55E"));
  const etichetta = errore ? "SCOLLEGATO" : (ms === null ? "…" : ms + " MS");

  return (
    <div className="relative" data-testid="spia-rete">
      <button
        onClick={() => setAperto((v) => !v)}
        className="inline-flex items-center gap-2 border px-3 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-[#8d9591] transition-colors hover:text-white"
        style={{ borderColor: "var(--lt-border)" }}
        title="Come stai viaggiando"
        data-testid="spia-rete-bottone"
      >
        <span
          data-testid="spia-rete-punto"
          className="inline-block h-2 w-2 rounded-full"
          style={{ background: colore, boxShadow: errore ? "none" : `0 0 6px ${colore}` }}
        />
        {etichetta}
      </button>

      {aperto && (
        <div
          className="absolute right-0 top-[calc(100%+8px)] w-[300px] border p-4 lt-glass"
          style={{ borderColor: "var(--lt-border)", background: "rgba(8,10,9,0.94)" }}
          data-testid="spia-rete-pannello"
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#8d9591]">Come stai viaggiando</p>
          <div className="mt-3 space-y-1.5 font-mono text-[11px] text-[#c8cfcb]">
            <p data-testid="spia-rete-nodo">Nodo: {HOST}</p>
            <p>Protocollo: {proto ? proto.toUpperCase() : "—"}</p>
            <p>Ritardo: {errore ? "nessuna risposta" : (ms === null ? "in misura…" : ms + " ms")}</p>
            {!!errore && <p style={{ color: "#ff5b5b" }}>Errore: {errore}</p>}
          </div>
          <p className="mt-3 text-[11px] leading-relaxed text-[#8d9591]">
            Da computer il <strong className="text-[#c8cfcb]">tunnel mimetizzato non c'è</strong>: quello vive
            dentro l'app Android, un browser non può avviarlo. Qui il traffico è HTTPS diretto verso il tuo
            nodo: i messaggi restano cifrati da un capo all'altro, ma chi guarda la rete vede che stai
            parlando con Lattice.
          </p>
          <button
            onClick={() => { setAperto(false); misura(); }}
            className="mt-3 w-full border px-3 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-[#c8cfcb] transition-colors hover:text-white"
            style={{ borderColor: "var(--lt-border)" }}
            data-testid="spia-rete-rimisura"
          >
            Misura di nuovo
          </button>
        </div>
      )}
    </div>
  );
}
